import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import { formatCurrency } from 'tio-ui/format';
import Button from 'tio-ui/components/button';
import { PhActivity, PhCreditCard, PhUploadSimple } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { IconBadgeColor } from 'tio-ui/components/icon-badge';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { courseCashMark } from 'tio-common/utils/course-cash-mark';
import type { SafeString } from '@ember/template';
import TaskCard from './task-card';
import type { TaskCardChipIntent } from './task-card';
import AnchorMessage from './anchor-message';
import CourseCashBody from './task-card/course-cash';
import SubmitEvidenceBody from './task-card/submit-evidence';
import type { SubmitEvidenceBundle } from './task-card/submit-evidence';
import type {
  AttentionModel,
  AttentionTask,
  AttentionTaskKind,
  AttentionTaskUrgency,
  AttentionAnchorKind,
} from 'tio-employee/utils/tas/attention-tasks';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

const noop = () => {};

type TaskIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

const TASK_ICONS: Record<AttentionTaskKind, TaskIcon> = {
  'use-course-cash': PhCreditCard,
  'upload-receipt': PhUploadSimple,
  'submit-evidence': PhUploadSimple,
};

const TASK_COLORS: Record<AttentionTaskKind, IconBadgeColor> = {
  'use-course-cash': 'purple',
  'upload-receipt': 'teal',
  'submit-evidence': 'teal',
};

// Deadline-driven urgency treatment (Figma: Task: Provide Your CourseCa$h™
// Receipt spec — Default/green, Warning/yellow, Error/red). Only tasks that
// set `urgency` (currently upload-receipt) override the kind-based defaults.
const URGENCY_ICON_COLORS: Record<AttentionTaskUrgency, IconBadgeColor> = {
  default: 'green',
  warning: 'amber',
  error: 'red',
};

const URGENCY_CHIP_INTENTS: Record<AttentionTaskUrgency, TaskCardChipIntent> = {
  default: 'success',
  warning: 'warning',
  error: 'danger',
};

const TASK_TITLE_KEYS: Record<AttentionTaskKind, string> = {
  'use-course-cash': 'tuition_assistance.applications.manage.tasks.use_course_cash_title',
  'upload-receipt': 'tuition_assistance.applications.manage.tasks.upload_receipt_title',
  'submit-evidence': 'tuition_assistance.applications.manage.tasks.submit_evidence_title',
};

// Task kinds whose title embeds the CourseCa$h™ brand mark and needs styling.
const COURSE_CASH_TASK_KINDS = new Set<AttentionTaskKind>(['use-course-cash', 'upload-receipt']);

interface TaskRow {
  task: AttentionTask;
  icon: TaskIcon;
  color: IconBadgeColor;
  chipIntent: TaskCardChipIntent;
  title: string | SafeString;
  chip?: string;
  /** MM/DD/YYYY label for the swipe this receipt task is for, when known — lets
   * concurrent upload-receipt cards read as distinct instead of identical. */
  transactionDateLabel?: string;
}

interface AnchorRow {
  kind: AttentionAnchorKind;
  title: string | SafeString;
  body?: string | SafeString;
  variant: 'dot' | 'glyph';
}

/** Just-for-fun confetti-burst treatment on the approved anchor's congratulations
 * card. Piece count only — per-piece color/direction/delay are pure CSS
 * (`:nth-child` rules in `app/styles/reimagined/tas.css`), so no inline styles. */
const CONFETTI_PIECE_COUNT = 20;
const CONFETTI_PIECES = Array.from({ length: CONFETTI_PIECE_COUNT });

interface NeedsYourAttentionSignature {
  Args: {
    model: AttentionModel;
    application: TasApplicationModel;
    onViewActivity?: () => void;
    onSubmitEvidence?: SubmitEvidenceBundle;
    onUploadReceipt?: (task: AttentionTask) => void;
  };
  Element: HTMLElement;
}

/**
 * The "Needs Your Attention" panel for the TAS course-management screen. Renders
 * the derived `AttentionModel` (see `utils/tas/attention-tasks`): informational
 * anchors first, then the non-completed action tasks as expandable cards. Renders
 * nothing when the model is not visible.
 */
export default class NeedsYourAttention extends Component<NeedsYourAttentionSignature> {
  @service declare intl: IntlService;

  get countLabel(): string {
    const { model } = this.args;
    if (model.pendingCount > 0) {
      return this.intl.t('tuition_assistance.applications.manage.tasks_pending', {
        count: model.pendingCount,
      });
    }
    // The all-caught-up empty state shows "(0)"; the submitted anchor shows no suffix.
    if (model.anchors.some((a) => a.kind === 'all-caught-up')) {
      return this.intl.t('tuition_assistance.applications.manage.empty_count', { count: 0 });
    }
    return '';
  }

  get anchorRows(): AnchorRow[] {
    const ns = 'tuition_assistance.applications.manage.anchors';
    return this.args.model.anchors.map((anchor) => {
      const kind: AttentionAnchorKind = anchor.kind;
      if (kind === 'approved') {
        return {
          kind,
          title: this.intl.t(`${ns}.approved_title`),
          body: this.intl.t(`${ns}.approved_body`),
          variant: 'dot',
        };
      }
      if (kind === 'course-cash-used') {
        return {
          kind,
          title: courseCashMark(this.intl.t(`${ns}.course_cash_used_title`)),
          body: courseCashMark(this.intl.t(`${ns}.course_cash_used_body`)),
          variant: 'dot',
        };
      }
      if (kind === 'all-caught-up') {
        return {
          kind,
          title: this.intl.t(`${ns}.caught_up_title`),
          body: this.intl.t(`${ns}.caught_up_body`),
          variant: 'glyph',
        };
      }
      return {
        kind,
        title: this.intl.t(`${ns}.submitted_title`),
        body: this.intl.t(`${ns}.submitted_body`),
        variant: 'dot',
      };
    });
  }

  get taskRows(): TaskRow[] {
    return this.args.model.tasks
      .filter((task) => task.status !== 'completed')
      .map((task) => ({
        task,
        icon: TASK_ICONS[task.kind],
        color: task.urgency ? URGENCY_ICON_COLORS[task.urgency] : TASK_COLORS[task.kind],
        chipIntent: task.urgency ? URGENCY_CHIP_INTENTS[task.urgency] : 'info',
        title: this.titleFor(task),
        chip: this.chipFor(task),
        transactionDateLabel: this.transactionDateLabelFor(task),
      }));
  }

  /**
   * Concurrent upload-receipt tasks (one per swipe) otherwise render as
   * identical cards — fold the swipe amount into the title so they're
   * distinguishable even collapsed. Other kinds, and the single-task fallback
   * with no live amount data, use the plain kind-based title. CourseCash-brand
   * kinds also get the trademark-mark treatment (matches completedCourseCashTitle
   * and the course-cash-used anchor above).
   */
  titleFor = (task: AttentionTask): string | SafeString => {
    const raw =
      task.kind === 'upload-receipt' && task.amount != null
        ? this.intl.t(
            'tuition_assistance.applications.manage.tasks.upload_receipt_title_for_amount',
            { amount: formatCurrency(task.amount / 100) }
          )
        : this.intl.t(TASK_TITLE_KEYS[task.kind]);
    return COURSE_CASH_TASK_KINDS.has(task.kind) ? courseCashMark(raw) : raw;
  };

  /** MM/DD/YYYY from a YYYY-MM-DD(THH:..) string, parsed as a local calendar
   * date (see chipFor's note) — not the chip's compact MM/DD, since the body
   * has room for the full date. */
  transactionDateLabelFor = (task: AttentionTask): string | undefined => {
    if (!task.transactionDate) {
      return undefined;
    }
    const datePart = task.transactionDate.split('T')[0] ?? '';
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) {
      return undefined;
    }
    return `${month}/${day}/${year}`;
  };

  /**
   * Once CourseCash is used its task completes and leaves taskRows, but the
   * card must stay reachable (spent/remaining/details) — the "drawer below"
   * that the course-cash-used anchor promises (elevator-pitch#211).
   */
  get completedCourseCashTask(): AttentionTask | undefined {
    return this.args.model.tasks.find(
      (task) => task.kind === 'use-course-cash' && task.status === 'completed'
    );
  }

  /** Styled title for the completed-course-cash drawer card. */
  get completedCourseCashTitle(): SafeString {
    return courseCashMark(
      this.intl.t('tuition_assistance.applications.manage.tasks.use_course_cash_title')
    );
  }

  get spentLabel(): string {
    const amounts = this.args.application.amounts as
      { course_cash?: { used?: number } } | undefined;
    return formatCurrency((amounts?.course_cash?.used ?? 0) / 100);
  }

  chipFor = (task: AttentionTask): string | undefined => {
    if (!task.dueDate) {
      return undefined;
    }
    // Parse the calendar date from the YYYY-MM-DD string directly. Going through
    // `new Date(...).getDate()` reads a UTC-parsed date in local time, which can
    // render an off-by-one day in negative-UTC timezones.
    const datePart = task.dueDate.split('T')[0] ?? '';
    const [, month, day] = datePart.split('-');
    if (!month || !day) {
      return undefined;
    }
    const formatted = `${month}/${day}`;
    const key =
      task.kind === 'use-course-cash'
        ? 'tuition_assistance.applications.manage.tasks.use_by'
        : 'tuition_assistance.applications.manage.tasks.due';
    return this.intl.t(key, { date: formatted });
  };

  /** Available CourseCash balance in dollars (the model stores cents). */
  get cardBalanceDollars(): number {
    return (this.args.application.courseCashAvailableBalance ?? 0) / 100;
  }

  get cardBalanceLabel(): string {
    return formatCurrency(this.cardBalanceDollars);
  }

  get institutionName(): string {
    const value = getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.args.application.tasProgramInstance?.fields
    );
    return typeof value === 'string' ? value : '';
  }

  /** Last 4 digits of the active CourseCash card, when issued. */
  get cardLast4(): string | undefined {
    const cards = this.args.application.pcsCards ?? [];
    const card = cards.find((c) => c.isActive) ?? cards[0];
    return card?.providerData?.last4;
  }

  <template>
    {{#if @model.visible}}
      <section class="tio-col gap-4" ...attributes>
        <div class="tio-row items-baseline gap-2">
          <h3 class="tio-text-lg-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.heading"}}
          </h3>
          {{#if this.countLabel}}
            <span class="tio-text-sm-normal tio-fg-secondary">{{this.countLabel}}</span>
          {{/if}}
        </div>

        {{#each this.anchorRows as |anchor|}}
          {{#if (eq anchor.kind "approved")}}
            <div class="tio-confetti-burst-wrap">
              <AnchorMessage
                @title={{anchor.title}}
                @body={{anchor.body}}
                @variant={{anchor.variant}}
              />
              <div aria-hidden="true" class="tio-confetti-overlay">
                {{#each CONFETTI_PIECES}}
                  <span class="tio-confetti-piece"></span>
                {{/each}}
              </div>
            </div>
          {{else}}
            <AnchorMessage
              @title={{anchor.title}}
              @body={{anchor.body}}
              @variant={{anchor.variant}}
            />
          {{/if}}
        {{/each}}

        {{#each this.taskRows as |row|}}
          <TaskCard
            @icon={{row.icon}}
            @iconColor={{row.color}}
            @title={{row.title}}
            @chip={{row.chip}}
            @chipIntent={{row.chipIntent}}
            @defaultOpen={{eq row.task.kind "use-course-cash"}}
          >
            <:headerActions>
              {{#if (eq row.task.kind "use-course-cash")}}
                <Button
                  @appearance="minimal"
                  @intent="primary"
                  @size="sm"
                  {{on "click" (if @onViewActivity @onViewActivity noop)}}
                  data-test-view-card-activity
                >
                  <PhActivity @size={{16}} class="mr-1" />
                  {{t "tuition_assistance.course_cash.activity.view_activity"}}
                </Button>
              {{/if}}
            </:headerActions>
            <:body>
              {{#if (eq row.task.kind "use-course-cash")}}
                <CourseCashBody
                  @institutionName={{this.institutionName}}
                  @availableBalance={{this.cardBalanceLabel}}
                  @cardBalance={{this.cardBalanceDollars}}
                  @last4={{this.cardLast4}}
                />
              {{else if (eq row.task.kind "submit-evidence")}}
                <SubmitEvidenceBody @application={{@application}} @evidence={{@onSubmitEvidence}} />
              {{else}}
                <div class="tio-col gap-4">
                  <p class="tio-text-sm-normal tio-fg-secondary" data-test-upload-receipt-body>
                    {{#if row.transactionDateLabel}}
                      {{t
                        "tuition_assistance.applications.manage.tasks.upload_receipt_body_for_date"
                        date=row.transactionDateLabel
                      }}
                    {{else}}
                      {{t "tuition_assistance.applications.manage.tasks.upload_receipt_body"}}
                    {{/if}}
                  </p>
                  <div>
                    <Button
                      @intent="primary"
                      data-test-upload-receipt-cta
                      {{on "click" (fn (if @onUploadReceipt @onUploadReceipt noop) row.task)}}
                    >
                      <PhUploadSimple @size={{16}} class="mr-2" />
                      {{t "tuition_assistance.applications.manage.tasks.upload_receipt_cta"}}
                    </Button>
                  </div>
                </div>
              {{/if}}
            </:body>
          </TaskCard>
        {{/each}}

        {{#if this.completedCourseCashTask}}
          <TaskCard
            @icon={{PhCreditCard}}
            @iconColor="purple"
            @title={{this.completedCourseCashTitle}}
            data-test-course-cash-drawer
          >
            <:headerActions>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                {{on "click" (if @onViewActivity @onViewActivity noop)}}
                data-test-view-card-activity
              >
                <PhActivity @size={{16}} class="mr-1" />
                {{t "tuition_assistance.course_cash.activity.view_activity"}}
              </Button>
            </:headerActions>
            <:body>
              <CourseCashBody
                @used={{true}}
                @institutionName={{this.institutionName}}
                @spentAmount={{this.spentLabel}}
                @remainingAmount={{this.cardBalanceLabel}}
                @cardBalance={{this.cardBalanceDollars}}
                @last4={{this.cardLast4}}
              />
            </:body>
          </TaskCard>
        {{/if}}
      </section>
    {{/if}}
  </template>
}
